/**
 * ClientSelector - Reusable combobox for selecting a client
 * Used in reports, trends, and other pages that need client selection
 */
import { useState, useMemo, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronsUpDown } from 'lucide-react';
import { useClientsStore } from '@/client/store/clientsStore';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/client/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/client/components/ui/popover';
import type { ClientResponse } from '@shared/types';

interface ClientSelectorProps {
  /** Currently selected client ID */
  value: string;
  /** Called when selection changes */
  onChange: (clientId: string, client: ClientResponse | null) => void;
  /** Whether to filter to active clients only (default: true) */
  activeOnly?: boolean;
  /** Optional label (default: "Client") */
  label?: string;
  /** Additional class for the container */
  className?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function ClientSelector({
  value,
  onChange,
  activeOnly = true,
  label = 'Client',
  className,
  disabled = false,
}: ClientSelectorProps) {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { clients, fetchClients } = useClientsStore();
  const [open, setOpen] = useState(false);

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const token = await getAccessTokenSilently();
        await fetchClients(token, logout);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    loadClients();
  }, [getAccessTokenSilently, fetchClients]);

  // Filter to active clients if requested
  const filteredClients = useMemo(() => {
    return activeOnly ? clients.filter(c => c.active === 1) : clients;
  }, [clients, activeOnly]);

  const selectedClient = useMemo(() => {
    return filteredClients.find(c => c.clientID === value) || null;
  }, [filteredClients, value]);

  const handleSelect = (clientId: string) => {
    const newValue = clientId === value ? '' : clientId;
    const client = newValue ? filteredClients.find(c => c.clientID === newValue) || null : null;
    onChange(newValue, client);
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-10 w-full justify-between font-normal overflow-hidden"
          >
            <span className="truncate">
              {value
                ? `${value} - ${selectedClient?.name}`
                : 'Select client...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search clients..." />
            <CommandList>
              <CommandEmpty>No client found.</CommandEmpty>
              <CommandGroup>
                {filteredClients.map((client) => (
                  <CommandItem
                    key={client.clientID}
                    value={`${client.clientID} ${client.name}`}
                    onSelect={() => handleSelect(client.clientID)}
                    className={cn(value === client.clientID && 'bg-accent')}
                  >
                    <span className="font-mono text-muted-foreground shrink-0">{client.clientID}</span>
                    <span className="truncate">{client.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Hook for managing client selection state
 * Returns state and handlers for use with ClientSelector
 */
export function useClientSelection() {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);

  const handleClientChange = (clientId: string, client: ClientResponse | null) => {
    setSelectedClientId(clientId);
    setSelectedClient(client);
  };

  return {
    selectedClientId,
    selectedClient,
    handleClientChange,
    clearSelection: () => {
      setSelectedClientId('');
      setSelectedClient(null);
    },
  };
}
